import { useUserData } from '@flik/shared';
import { useReactQuery } from '@flik/shared/api';
import { Tooltip, UserCircularAvatar, useTheme } from '@flik/shared/components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MagicModal from '../magic/components/MagicModal';

// Simple markdown renderer component
const MarkdownRenderer: React.FC<{ content: string; className?: string }> = ({
  content,
  className = '',
}) => {
  const { provideTailwind } = useTheme();

  const renderMarkdown = (text: string) => {
    // Preprocess: Remove leading ```markdown (or ```) and trailing ``` if the entire content is wrapped
    let processedText = text.trim();
    const leadingCodeBlockMatch = processedText.match(/^```(\w+)?\s*\n?/);
    const trailingCodeBlockMatch = processedText.match(/\n?```$/);

    if (leadingCodeBlockMatch && trailingCodeBlockMatch) {
      // Remove leading ```markdown or ```
      processedText = processedText.substring(leadingCodeBlockMatch[0].length);
      // Remove trailing ```
      processedText = processedText
        .substring(0, processedText.length - 3)
        .trim();
    }

    const elements: React.ReactNode[] = [];
    let keyCounter = 0;

    // First, extract and replace multi-line code blocks with placeholders
    const codeBlocks: string[] = [];
    const codeBlockPlaceholder = (index: number) => `__CODE_BLOCK_${index}__`;

    // Match code blocks: ```language\ncontent\n``` or ```\ncontent\n```
    // This handles both with and without language identifier
    // The regex captures: opening ```, optional language, optional whitespace, newline, content, closing ```
    const codeBlockRegex = /```\w*\s*\n([\s\S]*?)```/g;
    let textWithPlaceholders = processedText.replace(
      codeBlockRegex,
      (match, code) => {
        const index = codeBlocks.length;
        // Extract only the content between the backticks, removing any leading/trailing whitespace
        const cleanCode = code.trim();
        codeBlocks.push(cleanCode);
        return codeBlockPlaceholder(index);
      },
    );

    // Split by lines and process each line
    const lines = textWithPlaceholders.split('\n');
    let currentParagraph: string[] = [];

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const paragraphText = currentParagraph.join('\n');
        // Handle bold text
        let processedText = paragraphText.replace(
          /\*\*(.*?)\*\*/g,
          '<strong>$1</strong>',
        );
        // Handle italic text (but not inside bold)
        processedText = processedText.replace(
          /(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g,
          '<em>$1</em>',
        );
        // Handle inline code (single backticks, but not triple backticks)
        processedText = processedText.replace(
          /(?<!`)`([^`]+)`(?!`)/g,
          (match, code) => {
            // Escape HTML in inline code
            const escapedCode = code
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;');
            return `<code class="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm">${escapedCode}</code>`;
          },
        );
        // Replace code block placeholders (shouldn't happen in paragraphs, but handle it)
        processedText = processedText.replace(
          /__CODE_BLOCK_(\d+)__/g,
          (match, index) => {
            const code = codeBlocks[parseInt(index)];
            // Escape HTML in code blocks
            const escapedCode = code
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;');
            return `<pre class="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm overflow-x-auto mb-2"><code>${escapedCode}</code></pre>`;
          },
        );

        if (processedText.trim()) {
          elements.push(
            <p
              key={keyCounter++}
              className={`mb-2 ${provideTailwind('text-secondary')}`}
              dangerouslySetInnerHTML={{ __html: processedText }}
            />,
          );
        }
        currentParagraph = [];
      }
    };

    lines.forEach((line) => {
      // Check if line is a code block placeholder
      const codeBlockMatch = line.match(/^__CODE_BLOCK_(\d+)__$/);
      if (codeBlockMatch) {
        flushParagraph();
        const code = codeBlocks[parseInt(codeBlockMatch[1])];
        // Escape HTML in code blocks
        const escapedCode = code
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        elements.push(
          <pre
            key={keyCounter++}
            className={`bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm overflow-x-auto mb-2 ${provideTailwind(
              'text-primary',
            )}`}
          >
            <code>{escapedCode}</code>
          </pre>,
        );
        return;
      }

      // Handle headers
      if (line.startsWith('### ')) {
        flushParagraph();
        elements.push(
          <h3
            key={keyCounter++}
            className={`text-lg font-semibold mb-2 ${provideTailwind(
              'text-primary',
            )}`}
          >
            {line.replace('### ', '')}
          </h3>,
        );
        return;
      }
      if (line.startsWith('## ')) {
        flushParagraph();
        elements.push(
          <h2
            key={keyCounter++}
            className={`text-xl font-semibold mb-3 ${provideTailwind(
              'text-primary',
            )}`}
          >
            {line.replace('## ', '')}
          </h2>,
        );
        return;
      }
      if (line.startsWith('# ')) {
        flushParagraph();
        elements.push(
          <h1
            key={keyCounter++}
            className={`text-2xl font-bold mb-4 ${provideTailwind(
              'text-primary',
            )}`}
          >
            {line.replace('# ', '')}
          </h1>,
        );
        return;
      }

      // Handle bullet points
      if (line.startsWith('- ') || line.startsWith('* ')) {
        flushParagraph();
        elements.push(
          <li
            key={keyCounter++}
            className={`ml-4 mb-1 ${provideTailwind('text-secondary')}`}
          >
            {line.replace(/^[-*] /, '')}
          </li>,
        );
        return;
      }

      // Handle numbered lists
      if (/^\d+\. /.test(line)) {
        flushParagraph();
        elements.push(
          <li
            key={keyCounter++}
            className={`ml-4 mb-1 ${provideTailwind('text-secondary')}`}
          >
            {line.replace(/^\d+\. /, '')}
          </li>,
        );
        return;
      }

      // Empty line - flush current paragraph
      if (line.trim() === '') {
        flushParagraph();
        elements.push(<br key={keyCounter++} />);
        return;
      }

      // Add to current paragraph
      currentParagraph.push(line);
    });

    // Flush any remaining paragraph
    flushParagraph();

    return elements;
  };

  return <div className={className}>{renderMarkdown(content)}</div>;
};

interface InactiveSessionViewProps {
  sessionTitle?: string;
  sessionId?: string;
}

const InactiveSessionView: React.FC<InactiveSessionViewProps> = ({
  sessionTitle = 'Session',
  sessionId,
}) => {
  const { provideTailwind } = useTheme();
  const navigate = useNavigate();
  const apiClient = useReactQuery();
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [highlightedTranscriptIndex, setHighlightedTranscriptIndex] = useState<
    number | null
  >(null);

  // Refs for video and transcript container
  const videoRef = useRef<HTMLVideoElement>(null);
  const transcriptContainerRef = useRef<HTMLDivElement>(null);

  // Fetch recording data if sessionId is available
  const recordingQuery = apiClient.useQuery(
    'get',
    '/v1/sessions/{session_id}/recording',
    {
      params: {
        path: {
          session_id: sessionId || '',
        },
      },
      enabled: !!sessionId, // Only fetch if sessionId is provided
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: false, // Don't retry on failure to avoid unnecessary API calls
    },
  );

  // Fetch transcript data if sessionId is available
  const transcriptQuery = apiClient.useQuery(
    'get',
    '/v1/sessions/{session_id}/ai/transcript',
    {
      params: {
        path: {
          session_id: sessionId || '',
        },
      },
      enabled: !!sessionId, // Only fetch if sessionId is provided
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: false, // Don't retry on failure to avoid unnecessary API calls
    },
  );

  // Fetch AI summary data if sessionId is available
  const summaryQuery = apiClient.useQuery(
    'get',
    '/v1/sessions/{session_id}/ai/summary',
    {
      params: {
        path: {
          session_id: sessionId || '',
        },
      },
      enabled: !!sessionId, // Only fetch if sessionId is provided
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: false, // Don't retry on failure to avoid unnecessary API calls
    },
  );

  // Get unique user IDs from transcript data
  const userIds = useMemo(() => {
    if (!transcriptQuery.data?.conversation) return [];
    return Array.from(
      new Set(
        transcriptQuery.data.conversation.map((chunk) => chunk.identity.id),
      ),
    );
  }, [transcriptQuery.data?.conversation]);

  // Use the user data hook to fetch and manage user information
  const {
    displayName,
    getUserDisplayName,
    debouncedGetMultipleUsersData,
    isUserDataCached,
  } = useUserData();

  // Track previous userIds to avoid unnecessary API calls
  const prevUserIdsRef = useRef<string[]>([]);
  // Track when user data fetch completes to trigger re-render
  const [userDataFetchKey, setUserDataFetchKey] = useState(0);

  // Preload user data for all participants when transcript data is available
  useEffect(() => {
    if (userIds.length > 0) {
      const currentUserIds = userIds.sort();
      const prevUserIds = prevUserIdsRef.current.sort();

      // Only fetch if userIds actually changed
      if (
        currentUserIds.length !== prevUserIds.length ||
        !currentUserIds.every((id, index) => id === prevUserIds[index])
      ) {
        console.log(
          'InactiveSessionView: Fetching user data for userIds:',
          userIds,
        );
        prevUserIdsRef.current = [...userIds];
        // Use debounced version to prevent rapid-fire API calls
        debouncedGetMultipleUsersData(userIds, 500).then(() => {
          // Update state to trigger re-render and re-check of areAllUsersLoaded
          setUserDataFetchKey((prev) => prev + 1);
        });
      } else {
        console.log(
          'InactiveSessionView: Skipping fetch - userIds unchanged:',
          userIds,
        );
      }
    }
  }, [userIds, debouncedGetMultipleUsersData]);

  // Check if all users in the transcript have their data loaded
  const areAllUsersLoaded = useMemo(() => {
    if (!transcriptQuery.data?.conversation || userIds.length === 0) {
      return true; // No transcript or no users, consider "loaded"
    }

    // Check if all user IDs have their data cached
    return userIds.every((userId) => isUserDataCached(userId));
  }, [
    transcriptQuery.data?.conversation,
    userIds,
    isUserDataCached,
    userDataFetchKey,
  ]);

  // Group consecutive transcript chunks by the same identity
  const groupedTranscriptChunks = useMemo(() => {
    if (!transcriptQuery.data?.conversation) return [];

    const groups: Array<{
      identity: (typeof transcriptQuery.data.conversation)[0]['identity'];
      chunks: Array<{
        text: string;
        timestamp: string;
        originalIndex: number;
      }>;
    }> = [];

    transcriptQuery.data.conversation.forEach((chunk, index) => {
      const lastGroup = groups[groups.length - 1];
      const identityKey = `${chunk.identity.type}-${chunk.identity.id}`;
      const lastIdentityKey = lastGroup
        ? `${lastGroup.identity.type}-${lastGroup.identity.id}`
        : null;

      // If this chunk has the same identity as the last group, add to it
      if (lastGroup && identityKey === lastIdentityKey) {
        lastGroup.chunks.push({
          text: chunk.text,
          timestamp: chunk.timestamp,
          originalIndex: index,
        });
      } else {
        // Start a new group
        groups.push({
          identity: chunk.identity,
          chunks: [
            {
              text: chunk.text,
              timestamp: chunk.timestamp,
              originalIndex: index,
            },
          ],
        });
      }
    });

    return groups;
  }, [transcriptQuery.data?.conversation]);

  const handleVideoLoad = () => {
    setIsVideoLoading(false);
    setVideoError(null);
  };

  const handleVideoError = () => {
    setIsVideoLoading(false);
    setVideoError('Failed to load video');
  };

  // Handle video time updates to highlight corresponding transcript
  const handleVideoTimeUpdate = () => {
    if (
      !videoRef.current ||
      !transcriptQuery.data?.conversation ||
      !recordingQuery.data?.startedAt
    )
      return;

    const currentTime = videoRef.current.currentTime;
    setCurrentVideoTime(currentTime);

    // Get recording start time
    const recordingStartTime =
      new Date(recordingQuery.data.startedAt).getTime() / 1000; // Convert to seconds

    // Find the transcript item that corresponds to the current video time
    const transcriptItems = transcriptQuery.data.conversation;
    let highlightedIndex: number | null = null;

    // Convert transcript timestamps to seconds relative to recording start
    // Timestamps represent when transcription was completed (after user finished speaking)
    // Select the most recent completed transcription at or before the current video time
    for (let i = 0; i < transcriptItems.length; i++) {
      const itemTimestamp =
        new Date(transcriptItems[i].timestamp).getTime() / 1000; // Convert to seconds
      const itemTimeRelativeToRecording = itemTimestamp - recordingStartTime;

      // Debug logging
      console.log(
        `Item ${i}: timestamp=${itemTimestamp}, recordingStart=${recordingStartTime}, relative=${itemTimeRelativeToRecording}, currentTime=${currentTime}`,
      );

      // Since timestamp is when transcription completed (after speaking),
      // we want the last completed transcription at or before current time
      if (itemTimeRelativeToRecording <= currentTime) {
        highlightedIndex = i; // keep advancing until we pass currentTime
      } else {
        break; // the list is chronological; we've gone past the current time
      }
    }

    // If we're before the first transcript item, highlight the first
    if (highlightedIndex === null && transcriptItems.length > 0) {
      highlightedIndex = 0;
    }
    // If we're past all transcript items, highlightedIndex will already be the last matched

    console.log(
      `Highlighting index: ${highlightedTranscriptIndex} -> ${highlightedIndex}`,
    );
    setHighlightedTranscriptIndex(highlightedIndex);
  };

  // Auto-scroll to highlighted transcript item (now works with grouped chunks)
  useEffect(() => {
    if (
      highlightedTranscriptIndex !== null &&
      transcriptContainerRef.current &&
      groupedTranscriptChunks.length > 0
    ) {
      // Find which group contains the highlighted chunk
      const groupIndex = groupedTranscriptChunks.findIndex((group) =>
        group.chunks.some(
          (chunk) => chunk.originalIndex === highlightedTranscriptIndex,
        ),
      );

      if (groupIndex !== -1) {
        const container = transcriptContainerRef.current;
        const highlightedElement = container.children[
          groupIndex
        ] as HTMLElement;

        if (highlightedElement) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = highlightedElement.getBoundingClientRect();

          // Check if element is not fully visible
          const isAboveView = elementRect.top < containerRect.top;
          const isBelowView = elementRect.bottom > containerRect.bottom;

          if (isAboveView || isBelowView) {
            highlightedElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
      }
    }
  }, [highlightedTranscriptIndex, groupedTranscriptChunks]);

  return (
    <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
      {/* Magic Modal at the top */}
      <MagicModal />

      {/* Header */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1
            className={`text-4xl md:text-5xl font-light mb-4 ${provideTailwind(
              'text-primary',
            )}`}
          >
            {sessionTitle}
          </h1>
          <p className={`text-xl ${provideTailwind('text-secondary')}`}>
            This session is currently inactive
          </p>
        </div>

        {/* Summary and Actions Section */}
        <div className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Actions Section - Left Column */}
          <div className="lg:col-span-1">
            <div
              className={`${provideTailwind(
                'bg-secondary/30',
              )} rounded-2xl p-6 border-0`}
            >
              <h2
                className={`text-xl font-medium mb-4 ${provideTailwind(
                  'text-primary',
                )}`}
              >
                Actions
              </h2>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Tooltip content="Sarah Chen" direction="up">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mt-1 flex-shrink-0 cursor-pointer">
                      <span className="text-white text-xs font-medium">SC</span>
                    </div>
                  </Tooltip>
                  <div className="flex-1">
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      Review and approve budget proposal for Q1 2024
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`text-xs ${provideTailwind(
                            'text-tertiary',
                          )}`}
                        >
                          Due: Tomorrow
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${provideTailwind(
                            'bg-red-100',
                          )} ${provideTailwind('text-red-700')}`}
                        >
                          High
                        </span>
                      </div>
                      <span
                        className={`text-xs ${provideTailwind(
                          'text-tertiary',
                        )}`}
                      >
                        PROJ-1234
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Tooltip content="Mike Johnson" direction="up">
                    <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center mt-1 flex-shrink-0 cursor-pointer">
                      <span className="text-white text-xs font-medium">MJ</span>
                    </div>
                  </Tooltip>
                  <div className="flex-1">
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      Schedule team meeting to discuss project timeline
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`text-xs ${provideTailwind(
                            'text-tertiary',
                          )}`}
                        >
                          Due: Next week
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${provideTailwind(
                            'bg-yellow-100',
                          )} ${provideTailwind('text-yellow-700')}`}
                        >
                          Medium
                        </span>
                      </div>
                      <span
                        className={`text-xs ${provideTailwind(
                          'text-tertiary',
                        )}`}
                      >
                        PROJ-1235
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Tooltip content="Alex Rodriguez" direction="up">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1 flex-shrink-0 cursor-pointer">
                      <span className="text-white text-xs font-medium">AR</span>
                    </div>
                  </Tooltip>
                  <div className="flex-1">
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      Update documentation for new API endpoints
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`text-xs ${provideTailwind(
                            'text-tertiary',
                          )}`}
                        >
                          Due: End of month
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${provideTailwind(
                            'bg-green-100',
                          )} ${provideTailwind('text-green-700')}`}
                        >
                          Low
                        </span>
                      </div>
                      <span
                        className={`text-xs ${provideTailwind(
                          'text-tertiary',
                        )}`}
                      >
                        DOCS-456
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Tooltip content="Emma Wilson" direction="up">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-1 flex-shrink-0 cursor-pointer">
                      <span className="text-white text-xs font-medium">EW</span>
                    </div>
                  </Tooltip>
                  <div className="flex-1">
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      Prepare presentation for stakeholder review
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`text-xs ${provideTailwind(
                            'text-tertiary',
                          )}`}
                        >
                          Due: Friday
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${provideTailwind(
                            'bg-yellow-100',
                          )} ${provideTailwind('text-yellow-700')}`}
                        >
                          Medium
                        </span>
                      </div>
                      <span
                        className={`text-xs ${provideTailwind(
                          'text-tertiary',
                        )}`}
                      >
                        PRES-789
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Section - Right Column */}
          <div className="lg:col-span-2">
            <div
              className={`${provideTailwind(
                'bg-secondary/30',
              )} rounded-2xl p-6 border-0`}
            >
              <h2
                className={`text-xl font-medium mb-4 ${provideTailwind(
                  'text-primary',
                )}`}
              >
                Summary
              </h2>
              <div
                className="space-y-3 overflow-y-auto"
                style={{ height: 'calc(16rem + 2rem)' }}
              >
                {summaryQuery.isLoading ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      Loading summary...
                    </p>
                  </div>
                ) : summaryQuery.error ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-white"
                      >
                        <path d="M9 12l2 2 4-4" />
                        <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
                        <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
                        <path d="M13 12h3a2 2 0 0 1 2 2v1" />
                        <path d="M6 12H3a2 2 0 0 0-2 2v1" />
                      </svg>
                    </div>
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      Summary unavailable
                    </p>
                  </div>
                ) : summaryQuery.data?.item?.summary ? (
                  <MarkdownRenderer
                    content={summaryQuery.data.item.summary}
                    className="prose prose-sm max-w-none"
                  />
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-white"
                      >
                        <path d="M9 12l2 2 4-4" />
                        <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
                        <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
                        <path d="M13 12h3a2 2 0 0 1 2 2v1" />
                        <path d="M6 12H3a2 2 0 0 0-2 2v1" />
                      </svg>
                    </div>
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      No summary available
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recording Section - Left Column */}
          <div>
            <div
              className={`${provideTailwind(
                'bg-secondary/30',
              )} rounded-2xl p-6 border-0`}
            >
              <h2
                className={`text-xl font-medium mb-4 ${provideTailwind(
                  'text-primary',
                )}`}
              >
                Recording
              </h2>
              <div className="aspect-video bg-black rounded-xl flex items-center justify-center mb-4 relative overflow-hidden">
                {recordingQuery.isLoading ? (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-white"
                      >
                        <polygon points="5,3 19,12 5,21" />
                      </svg>
                    </div>
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      Loading recording...
                    </p>
                  </div>
                ) : recordingQuery.data?.downloadUrl && !videoError ? (
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover rounded-xl"
                    controls
                    onLoadStart={() => setIsVideoLoading(true)}
                    onLoadedData={handleVideoLoad}
                    onError={handleVideoError}
                    onTimeUpdate={handleVideoTimeUpdate}
                    preload="metadata"
                  >
                    <source
                      src={recordingQuery.data.downloadUrl}
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-white"
                      >
                        <polygon points="5,3 19,12 5,21" />
                      </svg>
                    </div>
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      {recordingQuery.error || videoError
                        ? 'Recording unavailable'
                        : 'No recording available'}
                    </p>
                  </div>
                )}
                {isVideoLoading && recordingQuery.data?.downloadUrl && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <p className={`text-sm ${provideTailwind('text-white')}`}>
                        Loading video...
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Transcript Section - Right Column */}
          <div>
            <div
              className={`${provideTailwind(
                'bg-secondary/30',
              )} rounded-2xl p-6 border-0`}
            >
              <h3
                className={`text-lg font-medium mb-4 ${provideTailwind(
                  'text-primary',
                )}`}
              >
                Transcript
              </h3>
              <div
                ref={transcriptContainerRef}
                className="space-y-3 overflow-y-auto"
                style={{ height: 'calc(16rem + 2rem)' }}
              >
                {transcriptQuery.isLoading ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      Loading transcript...
                    </p>
                  </div>
                ) : transcriptQuery.error ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-white"
                      >
                        <path d="M9 12l2 2 4-4" />
                        <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
                        <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
                        <path d="M13 12h3a2 2 0 0 1 2 2v1" />
                        <path d="M6 12H3a2 2 0 0 0-2 2v1" />
                      </svg>
                    </div>
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      Transcript unavailable
                    </p>
                  </div>
                ) : transcriptQuery.data?.conversation &&
                  transcriptQuery.data.conversation.length > 0 ? (
                  // Show loading state if users are still being loaded
                  !areAllUsersLoaded ? (
                    <div className="text-center py-8">
                      <div className="w-8 h-8 border-2 border-gray-300 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p
                        className={`text-sm ${provideTailwind(
                          'text-secondary',
                        )}`}
                      >
                        Loading participant information...
                      </p>
                    </div>
                  ) : (
                    groupedTranscriptChunks.map((group, groupIndex) => {
                      // Check if any chunk in this group is highlighted
                      const isHighlighted = group.chunks.some(
                        (chunk) =>
                          chunk.originalIndex === highlightedTranscriptIndex,
                      );

                      return (
                        <div
                          key={groupIndex}
                          className={`flex items-start space-x-3 p-3 rounded-lg transition-colors duration-200 ${
                            isHighlighted
                              ? 'bg-orange-100 dark:bg-orange-900/30'
                              : ''
                          }`}
                        >
                          <UserCircularAvatar
                            identity={group.identity}
                            size="sm"
                            tooltipPosition="up"
                            className="flex-shrink-0"
                          />
                          <div className="flex-1">
                            <p
                              className={`text-sm mb-1 ${provideTailwind(
                                'text-secondary',
                              )}`}
                            >
                              <span className="font-medium">
                                {group.identity.type === 'System' &&
                                group.identity.id === 'flikai'
                                  ? 'Flik AI'
                                  : getUserDisplayName(group.identity.id)}
                              </span>
                            </p>
                            <p className="text-sm">
                              {group.chunks.map((chunk, chunkIndex) => (
                                <span key={chunkIndex}>
                                  {chunk.text}
                                  {chunkIndex < group.chunks.length - 1 && ' '}
                                </span>
                              ))}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-white"
                      >
                        <path d="M9 12l2 2 4-4" />
                        <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
                        <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
                        <path d="M13 12h3a2 2 0 0 1 2 2v1" />
                        <path d="M6 12H3a2 2 0 0 0-2 2v1" />
                      </svg>
                    </div>
                    <p
                      className={`text-sm ${provideTailwind('text-secondary')}`}
                    >
                      No transcript available
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InactiveSessionView;
